import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  IconButton, 
  Box, 
  Typography, 
  Tooltip, 
  TextField, 
  InputAdornment, 
  Button, 
  Paper, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  TablePagination 
} from '@mui/material';
import { IoClose } from "react-icons/io5";
import { AiOutlineClose, AiOutlineSearch, AiOutlineInfoCircle } from "react-icons/ai";
import { PiFileCsv } from "react-icons/pi";
import dayjs from "dayjs";

const TopServicesModal = ({ open, onClose, services = [], formatCurrency }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleReset = () => {
    setSearchQuery("");
    setPage(0);
  };

  const filteredServices = services.filter((svc) =>
    (svc.service_id || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (svc.description || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedData = filteredServices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalDiscrepancy = services.reduce((acc, curr) => acc + (curr.discrepancy || 0), 0);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadCSV = () => {
    if (!filteredServices.length) return;

    const headers = ["Service ID", "Description", "Discrepancy Amount"];
    const csvRows = [headers.join(",")];

    filteredServices.forEach(row => {
      const values = [
        `"${(row.service_id || "").toString().replace(/"/g, '""')}"`,
        `"${(row.description || "").toString().replace(/"/g, '""')}"`,
        row.discrepancy || 0
      ];
      csvRows.push(values.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Top_Discrepant_Services_${dayjs().format("YYYYMMDD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };



  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 3, 
          overflow: 'hidden',
          minHeight: '60vh'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          fontWeight: 600,
          gap: 3,
          px: 3,
          py: 2.5
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', fontSize: '1.2rem' }}>
            Top Discrepant Services Breakdowns
          </Typography>
          <Tooltip 
            title="This list shows the services with the highest discrepancies after grouping. These discrepancies are calculated by comparing invoice data against contract amounts and SOFs at the service ID level."
            arrow 
            placement="top"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#7C2EA0' }}>
              <AiOutlineInfoCircle size={20} />
            </Box>
          </Tooltip>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1, justifyContent: 'flex-end' }}>
          <TextField
              size="small"
              placeholder="Search Service ID / Description..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              sx={{
                width: 350,
                "& .MuiInputBase-root": { height: "38px", fontSize: "14px" },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AiOutlineSearch size={22} />
                  </InputAdornment>
                ),
              }}
            />
            {searchQuery !== "" && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={handleReset}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  px: 2,
                  height: "38px",
                }}
              >
                Reset
              </Button>
            )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Download CSV" placement="top" arrow>
            <IconButton 
              onClick={handleDownloadCSV}
              disabled={filteredServices.length === 0}
              sx={{ 
                backgroundColor: "#f5f5f5",
                "&:hover": { backgroundColor: "#e0e0e0" },
                "&.Mui-disabled": { backgroundColor: "#f9f9f9", opacity: 0.5 }
              }}
            >
              <PiFileCsv size={20} color="#333" />
            </IconButton>
          </Tooltip>

          <IconButton onClick={onClose} sx={{ ml: 1, color: '#666' }}>
            <AiOutlineClose size={24} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 4 }}>
        <Box sx={{ flexGrow: 1 }}>
          {filteredServices.length > 0 ? (
            <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2, overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: "60vh" }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11, py: 2, px: 3 }}>
                        Service ID
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11, py: 2, px: 3 }}>
                        Description
                      </TableCell>
                      <TableCell align="right" sx={{ backgroundColor: "#7C2EA0", color: "#fff", fontWeight: 700, zIndex: 11, whiteSpace:"nowrap", py: 2, px: 3 }}>
                        Discrepancy Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.map((svc, index) => (
                      <TableRow 
                        key={index}
                        sx={{ 
                          "&:last-child td, &:last-child th": { border: 0 },
                          "&:hover": { backgroundColor: '#fcf9ff' }
                        }}
                      >
                        <TableCell sx={{ fontSize: "14px", fontWeight: 600, py: 1.5, px: 3 }}>
                          {svc.service_id}
                        </TableCell>
                        <TableCell sx={{ fontSize: "14px", color: "#444", py: 1.5, px: 3 }}>
                          {svc.description}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: "14px", fontWeight: 700, color: svc.discrepancy < 0 ? '#d32f2f' : '#388e3c', py: 1.5, px: 3 }}>
                          {formatCurrency(svc.discrepancy)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredServices.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ borderTop: '1px solid #eee' }}
              />
            </Paper>
          ) : (
            <Box sx={{ p: 10, textAlign: "center" }}>
              <Typography variant="body1" color="textSecondary" sx={{ fontStyle: "italic", fontSize: '1.1rem' }}>
                No services found matching your search.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TopServicesModal;
